// Libs
import classNames from 'classnames/bind';
import { TFunction } from 'i18next';
import { useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useEffect, useState } from 'react';
import { Avatar, Box, Grid2, Stack, Typography } from '@mui/material';
// Components, Layouts, Pages
import { BaseInputSearch, BaseStatus, BaseTable, BaseToolbar, Loading } from '~/components/common';
// Others
import useDebounce from '~/utils/hooks/useDebounce';
import { GRAY_200 } from '~/utils/constants/color';
import { StorageEnum } from '~/utils/enum/base';
import { useAppDispatch } from '~/utils/redux/hooks';
import { IHeaderCellType } from '~/utils/interface/common';
import { DateFormatEnum, MethodEnum, RoleEnum } from '~/utils/enum/common';
import { IPurchaseOrderDetail, IPurchaseOrderItem, IQuoteOrder } from '~/utils/interface/purchaseOrder';
import { DEFAULT_DEBOUNCE_DELAY_TIME, EMPTY_STRING, EMPTY_VALUE } from '~/utils/constants/common';
import { formatCurrencyUSD, formatDate, formatTitleCase, getFullName } from '~/utils/helper/common';
import { crewRouteAbsolute } from '~/utils/routes/roleRoutes/crew';
import { adminRouteAbsolute } from '~/utils/routes/roleRoutes/admin';
import { loaderRouteAbsolute } from '~/utils/routes/roleRoutes/loader';
import { foremanRouteAbsolute } from '~/utils/routes/roleRoutes/foreman';
import { getDetailPurchaseOrder } from '~/thunks/purchaseOrder/purchaseOrderThunk';
import { salesAdminRouteAbsolute } from '~/utils/routes/roleRoutes/salesAdmin';
import { salesPersonRouteAbsolute } from '~/utils/routes/roleRoutes/salesPerson';
import { warehouseLeadRouteAbsolute } from '~/utils/routes/roleRoutes/warehouseLead';
import { warehouseStaffRouteAbsolute } from '~/utils/routes/roleRoutes/warehouseStaff';
import { operationsManagerRouteAbsolute } from '~/utils/routes/roleRoutes/operationsManager';
// Styles, images, icons
import styles from './PurchaseOrderDetail.module.scss';

type Props = {};

const cx = classNames.bind(styles);

const columnsProductItem = (
  t: TFunction,
  order?: IQuoteOrder,
  onDetailOrder?: (id: string) => void
): IHeaderCellType<IPurchaseOrderItem>[] => {
  return [
    {
      id: 'image',
      label: t('purchase_order_detail_title_image'),
      render: (record: IPurchaseOrderItem) => (
        <Avatar
          src={
            record?.inventoryImages && record?.inventoryImages?.length > 0
              ? record?.inventoryImages[0].url
              : EMPTY_STRING
          }
          alt={t('common_alt_img', { ns: 'common/base' })}
          variant='rounded'
        />
      ),
    },
    {
      id: 'name',
      label: t('purchase_order_detail_title_name_table'),
      render: (record: IPurchaseOrderItem) => (
        <Stack flexDirection={'row'} gap={2} alignItems={'center'}>
          {record?.name || EMPTY_VALUE}

          {record?.isDelete && <Box className={cx('delete')}>{t('common_button_delete', { ns: 'common/base' })}</Box>}
        </Stack>
      ),
    },
    {
      id: 'quantity',
      label: t('purchase_order_detail_title_qty'),
      render: (record: IPurchaseOrderItem) => record?.quantity || EMPTY_VALUE,
    },
    {
      id: 'unitPrice',
      label: t('purchase_order_detail_title_price'),
      render: (record: IPurchaseOrderItem) =>
        formatCurrencyUSD({ value: record?.unitPrice, showDecimal: true }) || EMPTY_VALUE,
    },
    {
      id: 'total',
      label: t('purchase_order_detail_title_subtotal'),
      render: (record: IPurchaseOrderItem) =>
        formatCurrencyUSD({ value: record?.total, showDecimal: true }) || EMPTY_VALUE,
    },
    {
      id: 'orderId',
      label: t('purchase_order_detail_title_order_id'),
      render: (record: IPurchaseOrderItem) => {
        return (
          <Typography className={cx('textOrder')} onClick={() => onDetailOrder?.(order?.id || EMPTY_STRING)}>
            {order?.id ? `#${order?.id}` : EMPTY_VALUE}
          </Typography>
        );
      },
    },
  ];
};

const PurchaseOrderDetail = (props: Props) => {
  //#region Destructuring Props
  const {} = props;
  //#endregion Destructuring Props

  //#region Declare Hook
  const { t } = useTranslation(['admin/purchaseOrder', 'common/base']);
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const { id: purchaseOrderId } = useParams();
  //#endregion Declare Hook

  //#region Selector
  //#endregion Selector

  //#region Declare State
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [detailPurchaseOrder, setDetailPurchaseOrder] = useState<IPurchaseOrderDetail>();

  const [searchKey, setSearchKey] = useState<string>(EMPTY_STRING);
  const debounceSearchKey = useDebounce(searchKey, DEFAULT_DEBOUNCE_DELAY_TIME);

  const currentRole = JSON.parse(localStorage.getItem(StorageEnum.ROLE)!)[0];
  //#endregion Declare State

  //#region Declare Memo
  //#endregion Declare Memo

  //#region Implement Hook
  useEffect(() => {
    if (!purchaseOrderId) return;
    handleGetDetailPurchaseOrder(purchaseOrderId);

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [purchaseOrderId]);
  //#endregion Implement Hook

  //#region Handle Function
  const handleGetDetailPurchaseOrder = (purchaseOrderId: string) => {
    setIsLoading(true);

    const queryDetail = {
      id: purchaseOrderId,
      params: { method: MethodEnum.GET_DETAIL },
    };

    dispatch(getDetailPurchaseOrder(queryDetail))
      .unwrap()
      .then((res) => {
        const { data } = res;
        if (!data) return;

        setDetailPurchaseOrder(data);
      })
      .catch((err) => {})
      .finally(() => {
        setIsLoading(false);
      });
  };

  const renderItem = (title: string, children: React.ReactNode) => {
    return (
      <Stack
        direction={'row'}
        alignItems={'center'}
        justifyContent={'space-between'}
        borderBottom={1}
        borderColor={GRAY_200}
        height={50}
      >
        <Typography className={cx('textLabel')}>{title}</Typography>

        <Stack width={'60%'} direction={'row'} justifyContent={'flex-end'}>
          {children}
        </Stack>
      </Stack>
    );
  };

  const handleSearchChange = (value: string) => {
    setSearchKey(value);
  };

  const handleDetailOrder = (orderId?: string) => {
    if (!orderId) return;

    switch (currentRole) {
      case RoleEnum.SUPER_ADMIN:
        navigate(`${adminRouteAbsolute.detailOrder(orderId)}`);
        break;
      case RoleEnum.SALES_ADMIN:
        navigate(`${salesAdminRouteAbsolute.detailOrder(orderId)}`);
        break;
      case RoleEnum.SALES_PERSON:
        navigate(`${salesPersonRouteAbsolute.detailOrder(orderId)}`);
        break;
      case RoleEnum.OPERATIONS_MANAGER:
        navigate(`${operationsManagerRouteAbsolute.detailOrder(orderId)}`);
        break;
      case RoleEnum.WAREHOUSE_LEAD:
        navigate(`${warehouseLeadRouteAbsolute.detailOrder(orderId)}`);
        break;
      case RoleEnum.WAREHOUSE_STAFF:
        navigate(`${warehouseStaffRouteAbsolute.detailOrder(orderId)}`);
        break;
      case RoleEnum.FOREMAN:
        navigate(`${foremanRouteAbsolute.detailOrder(orderId)}`);
        break;
      case RoleEnum.CREW:
        navigate(`${crewRouteAbsolute.detailOrder(orderId)}`);
        break;
      case RoleEnum.LOADER:
        navigate(`${loaderRouteAbsolute.detailOrder(orderId)}`);
        break;
    }
  };
  //#endregion Handle Function

  return (
    <Box id='purchaseOrderDetailPage' className={cx('container')}>
      <BaseToolbar title={t('purchase_order_detail_title_purchase_order_details')} isBack />

      <Box component={'div'} className={cx('body')}>
        <Grid2 container direction={'row'} spacing={6}>
          <Grid2 size={6}>
            <Stack className={cx('viewBox')} padding={4}>
              <Typography className={cx('textTitle')}>
                {t('purchase_order_detail_title_purchase_order_details')}
              </Typography>

              {renderItem(
                t('purchase_order_detail_title_status'),
                <Typography className={cx('textValue')}>
                  {detailPurchaseOrder?.status ? <BaseStatus label={detailPurchaseOrder?.status} /> : EMPTY_VALUE}
                </Typography>
              )}

              {renderItem(
                t('purchase_order_detail_title_needed_by'),
                <Typography className={cx('textValue')}>
                  {detailPurchaseOrder?.neededByDate
                    ? formatDate(detailPurchaseOrder?.neededByDate, DateFormatEnum.MM_DD_YYYY)
                    : EMPTY_VALUE}
                </Typography>
              )}

              {renderItem(
                t('purchase_order_detail_title_delivery_method'),
                <Typography className={cx('textValue')}>
                  {formatTitleCase(detailPurchaseOrder?.method || EMPTY_STRING) || EMPTY_VALUE}
                </Typography>
              )}

              {renderItem(
                t('purchase_order_detail_title_notes'),
                <Typography className={cx('textValue')}>{detailPurchaseOrder?.note || EMPTY_VALUE}</Typography>
              )}
            </Stack>
          </Grid2>

          <Grid2 size={6}>
            <Stack className={cx('viewBox')} padding={4}>
              <Typography className={cx('textTitle')}>{t('purchase_order_detail_title_vendor_details')}</Typography>

              {renderItem(
                t('purchase_order_detail_title_name'),
                <Typography className={cx('textValue')}>
                  {detailPurchaseOrder?.vendor?.vendorName || EMPTY_VALUE}
                </Typography>
              )}

              {renderItem(
                t('purchase_order_detail_title_contact_name'),
                <Typography className={cx('textValue')}>
                  {getFullName({
                    firstName: detailPurchaseOrder?.approvedBy?.firstName,
                    lastName: detailPurchaseOrder?.approvedBy?.lastName,
                  }) || EMPTY_VALUE}
                </Typography>
              )}

              {renderItem(
                t('purchase_order_detail_title_email'),
                <Typography className={cx('textValue')}>
                  {detailPurchaseOrder?.approvedBy?.email || EMPTY_VALUE}
                </Typography>
              )}

              {renderItem(
                t('purchase_order_detail_title_phone'),
                <Typography className={cx('textValue')}>{EMPTY_VALUE}</Typography>
              )}

              {renderItem(
                t('purchase_order_detail_title_address'),
                <Typography className={cx('textValue')}>{EMPTY_VALUE}</Typography>
              )}
            </Stack>
          </Grid2>
        </Grid2>

        <Stack className={cx('viewBox')} px={4} py={5} gap={4}>
          <Stack direction={'row'} justifyContent={'space-between'} alignItems={'center'}>
            <Typography className={cx('textTitle')}>{t('purchase_order_detail_title_items')}</Typography>

            <BaseInputSearch
              width={300}
              typeStyle='outlined'
              onChange={handleSearchChange}
              borderRadius={100}
              placeholder={t('common_text_search', { ns: 'common/base' })}
            />
          </Stack>

          <BaseTable
            dataSource={detailPurchaseOrder?.purchaseOrderItems}
            // currentPage={pageSelected}
            // totalPage={pagination?.totalPages}
            columns={columnsProductItem(t, detailPurchaseOrder?.order, handleDetailOrder)}
            // onPageChange={handlePageChange}
          />
        </Stack>
      </Box>

      {isLoading && <Loading />}
    </Box>
  );
};

export default PurchaseOrderDetail;
